import { CartAddEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';
import { ADD_TO_CART_TEXT_ANIMATION_DURATION } from '@theme/product-form';

/**
 * A single product in a look. The options use the product page picker's radio
 * markup; when they change, the matching variant is found from the product's
 * variant list. The button adds it through the theme's own flow: POST to the
 * cart with the cart section ids, then dispatch CartAddEvent so the drawer
 * refreshes.
 */
class LookProductComponent extends HTMLElement {
  /** @type {Array<{id:number, available:boolean, price:number, options:string[]}>} */
  #variants = [];

  /** @type {number | undefined} */
  #addedTimeout;

  connectedCallback() {
    const data = this.querySelector('[ref="variants"]');
    if (!data) return;

    try {
      this.#variants = JSON.parse(data.textContent || '[]');
    } catch (_) {
      this.#variants = [];
    }

    // An option with a single value is chosen for the shopper
    this.#fieldsets().forEach((fieldset) => {
      const select = fieldset.querySelector('select');
      if (select) {
        const values = [...select.options].filter((option) => option.value);
        if (values.length === 1) values[0].selected = true;
        return;
      }
      const inputs = fieldset.querySelectorAll('input[type="radio"]');
      if (inputs.length === 1) /** @type {HTMLInputElement} */ (inputs[0]).checked = true;
    });

    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);
    this.#refresh();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    this.removeEventListener('click', this.#onClick);
  }

  #fieldsets() {
    return [...this.querySelectorAll('.look-product__option')];
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const input = event.target;
    if (input instanceof HTMLSelectElement) {
      this.#refresh();
      return;
    }
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;

    const legendValue = input.closest('fieldset')?.querySelector('[ref="swatchValue"]');
    if (legendValue) legendValue.textContent = input.value;

    this.#refresh();
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const add = /** @type {HTMLElement} */ (event.target).closest('.look-product__add');
    if (add instanceof HTMLButtonElement && !add.disabled) this.#add(add);
  };

  #chosen() {
    return this.#fieldsets().map((fieldset) => {
      const select = fieldset.querySelector('select');
      if (select) return select.value || null;
      const checked = /** @type {HTMLInputElement | null} */ (fieldset.querySelector('input:checked'));
      return checked ? checked.value : null;
    });
  }

  #refresh() {
    const add = /** @type {HTMLButtonElement | null} */ (this.querySelector('[ref="add"]'));
    const fieldsets = this.#fieldsets();
    if (!add || !fieldsets.length) return;

    const chosen = this.#chosen();
    this.#markAvailability(chosen);

    const variant = chosen.includes(null)
      ? null
      : this.#variants.find((v) => chosen.every((value, i) => v.options[i] === value)) || null;

    if (!variant) {
      add.disabled = true;
      delete add.dataset.variantId;
      this.#setLabel(add, this.dataset.labelChoose || 'Select a size');
      return;
    }

    const price = this.querySelector('[ref="price"]');
    if (price) price.textContent = this.#money(variant.price);

    if (variant.available) {
      add.disabled = false;
      add.dataset.variantId = String(variant.id);
      this.#setLabel(add, this.dataset.labelAdd || 'Add to bag');
    } else {
      add.disabled = true;
      delete add.dataset.variantId;
      this.#setLabel(add, this.dataset.labelSoldOut || 'Sold out');
    }
  }

  /**
   * Once a colour is picked, sizes that colour has no stock in are marked the
   * way the product page marks them, and the same the other way round.
   * @param {Array<string|null>} chosen
   */
  #markAvailability(chosen) {
    this.#fieldsets().forEach((fieldset, index) => {
      fieldset.querySelectorAll('select option[value]').forEach((node) => {
        const option = /** @type {HTMLOptionElement} */ (node);
        if (!option.value) return;
        const available = this.#isAvailable(index, option.value, chosen);
        option.textContent = (available ? option.dataset.label : option.dataset.labelUnavailable) || option.value;
      });
      fieldset.querySelectorAll('input[type="radio"]').forEach((node) => {
        const input = /** @type {HTMLInputElement} */ (node);
        const available = this.#isAvailable(index, input.value, chosen);
        input.dataset.optionAvailable = String(available);
        if (available) input.removeAttribute('aria-disabled');
        else input.setAttribute('aria-disabled', 'true');
      });
    });
  }

  /**
   * @param {number} index
   * @param {string} value
   * @param {Array<string|null>} chosen
   */
  #isAvailable(index, value, chosen) {
    return this.#variants.some(
      (v) => v.available && v.options[index] === value && chosen.every((other, i) => i === index || other === null || v.options[i] === other)
    );
  }

  /**
   * Writes into the label span so the "Added" span beside it survives.
   * @param {HTMLButtonElement} button
   * @param {string} text
   */
  #setLabel(button, text) {
    const label = button.querySelector('[ref="addLabel"]');
    if (label) label.textContent = text;
    else button.textContent = text;
  }

  /**
   * Plays the product page's "Added" swap on the button, then puts it back.
   * @param {HTMLButtonElement} button
   */
  #animateAdded(button) {
    if (this.#addedTimeout) clearTimeout(this.#addedTimeout);
    button.classList.add('atc-added');
    this.#addedTimeout = window.setTimeout(() => button.classList.remove('atc-added'), ADD_TO_CART_TEXT_ANIMATION_DURATION);
  }

  /** @param {number} cents */
  #money(cents) {
    const format = this.dataset.moneyFormat || '£{{amount}}';
    return format.replace(/\{\{\s*amount[a-z_]*\s*\}\}/, (cents / 100).toFixed(2));
  }

  /** @param {HTMLButtonElement} button */
  async #add(button) {
    const id = button.dataset.variantId;
    if (!id) return;

    const error = this.querySelector('[ref="error"]');
    if (error) error.hidden = true;

    button.disabled = true;
    button.classList.add('is-loading');
    button.setAttribute('aria-busy', 'true');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
      .filter(Boolean);

    const body = new FormData();
    body.set('id', id);
    body.set('quantity', '1');
    if (sectionIds.length) body.set('sections', [...new Set(sectionIds)].join(','));

    try {
      const response = await fetch(Theme.routes.cart_add_url, fetchConfig('javascript', { body }));
      const data = await response.json();

      if (data.status) {
        if (error) {
          error.textContent = data.description || data.message || '';
          error.hidden = false;
        }
        return;
      }

      let cart;
      try {
        cart = await (await fetch(`${Theme.routes.cart_url}.js`)).json();
      } catch (_) {
        /* the morphed cart section carries the totals regardless */
      }

      this.#animateAdded(button);

      document.dispatchEvent(
        new CartAddEvent(cart, this.id || 'look-product', {
          source: 'look-product',
          itemCount: cart?.item_count,
          sections: data.sections,
          productId: this.dataset.productId,
          variantId: id,
        })
      );
    } catch (_) {
      if (error) {
        error.textContent = 'Something went wrong. Please try again.';
        error.hidden = false;
      }
    } finally {
      button.disabled = false;
      button.classList.remove('is-loading');
      button.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('look-product-component')) {
  customElements.define('look-product-component', LookProductComponent);
}
